/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=79%3A1998
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import type { Interpolation, Theme } from '@emotion/react';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHeader, useHeaderWithLogoOnly } from './hooks';
import { LanguagePicker } from '../language-picker';
import { TypographyVariant } from '../../../component_library/components/Typography';
import { Button } from '../../../component_library/components/Button';
import { Link } from '../../../component_library/components/Link';
import { ButtonVariant } from '../../../component_library/components/Button/button-style-helper';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import StockPrice from '../../layout/stock-price-layout/StockPriceLayout';
import OperationButton from '../../OperationButton/OperationButton';
import { useOperationButton } from '../../OperationButton/hook';
import {
  FullPageWidthWrapper,
  HEADER_ID,
} from '../../../component_library/components/Grid';
import AlertBannerBox from '../alert-banners/alert-banners-box';
import HtmlBox from '../../../component_library/components/HtmlBox/HtmlBox';
import { ElementZIndexOrdering } from '../../../component_library/utils/element-z-iIndex-ordering';
import { useDetectPrinting } from '../../../hooks/use-detect-printing';
import { UserContextMenu } from '../user-context-menu';
import { useHeaderNavigationHeights } from './header-navigation-context-provider';
import ResourcesDropdown from './ResourcesDropdown';
import NavMobileList from './NavMobileList';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';
import { BoxWithId } from '../../../component_library/components/BoxWithId/BoxWithId';
import { LogoLink } from './LogoLink';

const HEADER_HEIGHT_PX = 72;

const Header: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);

  const {
    theme,
    muiTheme,
    isDesktop,
    isTabletMini,
    isMobile,
    companyLogo,
    homepagePath,
  } = useHeaderWithLogoOnly();
  const themeToken = useThemeToken();

  const {
    headerData,
    navigationMenu,
    navigationMenuLoading,
    navigationMenuError,
    loadingHeaderSection,
    errorFetchingHeaderSection,
    adminPageUrl,
    isAdminPageAvailable,
  } = useHeader();

  const { stickyHeaderRef } = useHeaderNavigationHeights();

  const { action: operationButtonAction } = useOperationButton();
  const { colors } = theme;

  useEffect(() => {
    const preventScroll = () => window.scrollTo(0, 0);

    if (open) {
      window.addEventListener('scroll', preventScroll);
    } else {
      window.removeEventListener('scroll', preventScroll);
    }
    return () => window.removeEventListener('scroll', preventScroll);
  }, [open]);

  const accountSettingWrapperStyles: Interpolation<Theme> = {
    width: '100%',
    backgroundColor: colors.secondary,
    zIndex: ElementZIndexOrdering.HEADER,
    padding: '12px 0',
  };
  const accountSettingStyles: Interpolation<Theme> = {
    display: 'flex',
    columnGap: isDesktop ? '48px' : '24px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    boxSizing: 'border-box',
    [muiTheme.breakpoints.only('desktop')]: {
      flexBasis: `${(10 / 12) * 100}%`,
    },
  };
  const stickyHeaderStyles = {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: ElementZIndexOrdering.HEADER_WITH_ALERT_BANNER_WRAPPER,
  };

  const navigationStyles: Interpolation<Theme> = {
    display: 'flex',
    boxSizing: 'border-box',
    justifyContent: 'center',
    backgroundColor: colors.grayscale.white,
    position: 'relative',
    zIndex: ElementZIndexOrdering.HEADER,
    [muiTheme.breakpoints.up('tablet')]: {
      boxShadow: '0px 1px 3px 0px #00000040',
    },
  };
  const navigationHamburgerMenuStyles: Interpolation<Theme> = {
    width: '100%',
    '& .MuiDivider-root': {
      display: 'none',
    },
  };
  const navigationStylesItem: Interpolation<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    [muiTheme.breakpoints.only('desktop')]: {
      flexBasis: `${(10 / 12) * 100}%`,
      padding: 0,
    },
  };
  const menuActionStyle: Interpolation<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '90px',
    backgroundColor: colors.grayscale.backgroundGrey,
    padding: '20px 0',
  };
  const hamburgerStyles: Interpolation<Theme> = {
    color: colors.secondary,
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    svg: { marginRight: '0 !important' },
  };

  const isPrinting = useDetectPrinting();

  // This stops the page from being covered with a loading screen when
  // the page is printed. Not sure exactly why this fixes the issue...
  const alertBannersBox = useMemo(
    () => (isPrinting ? null : <AlertBannerBox />),
    [isPrinting],
  );

  if (loadingHeaderSection || navigationMenuLoading) {
    return null;
  }

  if (
    navigationMenuError &&
    errorFetchingHeaderSection &&
    !navigationMenu &&
    !headerData
  ) {
    return <div>Something went wrong</div>;
  }

  const logo = !!companyLogo && (
    <LogoLink homepagePath={homepagePath} style={{ maxHeight: '100%' }}>
      <img
        src={companyLogo}
        alt=""
        style={{
          maxHeight: `${HEADER_HEIGHT_PX}px`,
          maxWidth: '130px',
          boxSizing: 'border-box',
          padding: '8px 0',
          height: 'auto',
          width: 'auto',
        }}
      />
    </LogoLink>
  );

  const showNavMobileList =
    navigationMenu?.resourcesLinksGroup ||
    navigationMenu?.cashlessParticipationLinkGroup ||
    isAdminPageAvailable;

  const blackBarLayout = headerData && (
    <BoxWithId
      sx={accountSettingWrapperStyles}
      dataElementId={DataElementId.HEADER_BLACK_BAR}
    >
      <FullPageWidthWrapper>
        <Box sx={[accountSettingStyles]}>
          <LanguagePicker hideTitle={isMobile} />
          {isAdminPageAvailable && !isMobile && (
            <Link
              variant={TypographyVariant.SMALL_BODY_BOLD}
              color={colors.grayscale.white}
              to={adminPageUrl}
              hideDecorations
              dataElementId={DataElementId.HEADER_LINK_ADMIN_VIEW}
              themeToken={themeToken}
            >
              {headerData.switchToAdminViewLinkText}
            </Link>
          )}
          <UserContextMenu
            theme={theme}
            themeToken={themeToken}
            homepagePath={homepagePath}
            loginButtonLabels={headerData.loginButtonLabels}
            myAccountLinkSign={headerData.myAccountLinkSign}
          />
        </Box>
      </FullPageWidthWrapper>
    </BoxWithId>
  );

  return isTabletMini || isMobile ? (
    <BoxWithId
      id={HEADER_ID}
      dataElementId={DataElementId.HEADER}
      ref={stickyHeaderRef}
      sx={stickyHeaderStyles}
    >
      <Box sx={navigationHamburgerMenuStyles}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: `calc(${HEADER_HEIGHT_PX}px)`,
            backgroundColor: colors.grayscale.white,
            position: 'relative',
            zIndex: ElementZIndexOrdering.HEADER,
            padding: isMobile ? ' 0 24px' : ' 0 48px',
            boxShadow: '0px 1px 3px 0px #00000040',
          }}
        >
          {logo}
          {showNavMobileList && (
            <Button
              variant={ButtonVariant.FLAT}
              themeToken={themeToken}
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              icon={
                <FontAwesomeIcon icon={open ? faXmark : faBars} size="1x" />
              }
              additionalStyles={hamburgerStyles}
              dataElementId={DataElementId.HEADER_BUTTON_OPEN_MOBILE_MENU}
            />
          )}
        </Box>
        {/* The white open menu for mobile devices */}
        <Box
          sx={{
            display: open ? 'display' : 'none',
            width: '100%',
            height: `calc(100dvh - calc(${HEADER_HEIGHT_PX}px))`,
            overflow: open ? 'auto' : 'hidden',
            position: open ? 'sticky' : '',
            zIndex: ElementZIndexOrdering.HEADER_MENU_DROPDOWN,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              backgroundColor: colors.grayscale.white,
            }}
          >
            {!!operationButtonAction && (
              <div css={menuActionStyle}>
                <OperationButton
                  dataElementId={DataElementId.HEADER_BUTTON_ENROLL_NOW}
                />
              </div>
            )}
            {navigationMenu && showNavMobileList && (
              <NavMobileList
                firstSectionTitle={navigationMenu?.resourcesLinksGroupTitle}
                firstSectionLinks={navigationMenu?.resourcesLinksGroup ?? []}
                secondSectionTitle={
                  navigationMenu?.cashlessParticipationLinkGroupTitle
                }
                secondSectionLinks={
                  navigationMenu?.cashlessParticipationLinkGroup ?? []
                }
                secondSectionNew={
                  navigationMenu?.isSecondSectionNewLabel || false
                }
                thirdSectionTitle={
                  isAdminPageAvailable && isMobile ? (
                    <HtmlBox html={navigationMenu.viewCategoryTitle} />
                  ) : undefined
                }
                thirdSectionLinks={
                  isAdminPageAvailable && isMobile
                    ? [
                      {
                        path: adminPageUrl,
                        label: headerData?.switchToAdminViewLinkText ?? '',
                        icon: 'fa fa-bar-chart',
                      },
                    ]
                    : undefined
                }
                theme={theme}
                additionalInfo={<StockPrice />}
                additionalStyles={{
                  padding: isMobile ? ' 0 24px 25px' : ' 0 48px 25px',
                  paddingTop: operationButtonAction ? '20px' : '40px',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
      {blackBarLayout}
      {alertBannersBox}
    </BoxWithId>
  ) : (
    <>
      {blackBarLayout}
      <BoxWithId
        id={HEADER_ID}
        dataElementId={DataElementId.HEADER}
        ref={stickyHeaderRef}
        sx={stickyHeaderStyles}
      >
        <Box sx={navigationStyles}>
          <FullPageWidthWrapper>
            <Box sx={[navigationStylesItem]}>
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: `${HEADER_HEIGHT_PX}px`,
                  }}
                >
                  <Box
                    sx={{
                      paddingRight: '94px',
                      minWidth: '100px',
                      maxHeight: '100%',
                    }}
                  >
                    {logo}
                  </Box>
                  {navigationMenu?.resourcesLinksGroup && (
                    <ResourcesDropdown
                      links={navigationMenu?.resourcesLinksGroup}
                      title={navigationMenu?.resourcesLearnAndEarnMoreTitle}
                      description={
                        navigationMenu?.resourcesLearnAndEarnMoreText
                      }
                      additionalInfo={<StockPrice />}
                      dropdownTitle={navigationMenu?.resourcesLinksGroupTitle}
                      additionalStyles={{ paddingRight: '40px' }}
                      theme={theme}
                      themeToken={themeToken}
                    />
                  )}
                  {!!navigationMenu?.cashlessParticipationLink?.path && (
                    <Link
                      to={navigationMenu?.cashlessParticipationLink.path}
                      variant={TypographyVariant.MEDIUM_BODY_BOLD}
                      color={theme.colors.secondary}
                      themeToken={themeToken}
                      hideDecorations
                      dataElementId={DataElementId.HEADER_LINK_ABOUT_CASHLESS}
                    >
                      {navigationMenu?.cashlessParticipationLink.label}
                    </Link>
                  )}
                </Box>
                <OperationButton
                  dataElementId={DataElementId.HEADER_BUTTON_ENROLL_NOW}
                />
              </>
            </Box>
          </FullPageWidthWrapper>
        </Box>
        {alertBannersBox}
      </BoxWithId>
    </>
  );
};

export default Header;
